import React, { useRef } from "react";
import styled, { css } from "styled-components";
import { NavLink, useNavigate } from "react-router-dom";
import brandLogoWhite from "../images/brand-logo-white.svg";
import brandLogo from "../images/brand-logo.svg";
import { Responsive } from "../responsive/Responsive";
import { MenuIcon, CloseIcon } from "./Icons";
import { StyledButton } from "./Button.styles";
import { resource } from "../localization/resource";

interface Props {
  invertHeader: boolean;
}

export const Header: React.VFC<Props> = ({ invertHeader }) => {
  const mobileMenu = useRef<HTMLDivElement>();
  const navigate = useNavigate();

  const handleOpenMenu = () => {
    mobileMenu.current.classList.add("open");
    document.body.classList.add("noscroll");
  };

  const handleCloseMenu = () => {
    mobileMenu.current?.classList.remove("open");
    document.body.classList.remove("noscroll");
  };

  const handleRegister = () => {
    handleCloseMenu();
    navigate("/register");
  };

  const renderMenu = (isMobile: boolean) => {
    return (
      <ul>
        <li className="secondary">
          <a href="https://www.equisoft.com/products/eapplications-esignatures-apply">{resource("common.about")}</a>
        </li>
        <li className="secondary">
          <a>{resource("common.login")}</a>
        </li>
        <li>
          {!isMobile ? (
            <StyledButton type="button" variant="primary" onClick={handleRegister}>
              {resource("common.register")}
            </StyledButton>
          ) : (
            <NavLink to="/register" onClick={handleCloseMenu}>
              {resource("common.register")}
            </NavLink>
          )}
        </li>
      </ul>
    );
  };

  return (
    <StyledHeader inverted={invertHeader}>
      <div className="container">
        <NavLink className="logo-link" to="/">
          <img src={invertHeader ? brandLogoWhite : brandLogo} alt="Equisoft" />
        </NavLink>
        <Responsive from="MD">
          <div className="menu">{renderMenu(false)}</div>
        </Responsive>
        <Responsive to="SM">
          <div className="menu">
            <button type="button" onClick={handleOpenMenu} className="mobile-button">
              <MenuIcon />
            </button>
          </div>
          <StyledMobileMenu ref={mobileMenu}>
            <div className="inner">
              <button type="button" onClick={handleCloseMenu}>
                <CloseIcon />
              </button>
              {renderMenu(true)}
            </div>
          </StyledMobileMenu>
        </Responsive>
      </div>
    </StyledHeader>
  );
};

type StyledHeaderProps = {
  inverted: boolean;
};

const StyledHeader = styled.nav<StyledHeaderProps>`
  position: sticky;
  top: 0;
  z-index: 1;
  padding: 25px 0 0;
  height: 100px;
  background-color: ${props => !props.inverted && "white"};
  box-shadow: ${props => !props.inverted && "0 0 2px rgba(0,0,0,0.1), 0 2px 8px rgba(0,0,0,.15)"};

  .logo-link {
    float: left;
    margin-top: ${props => (props.inverted ? "-5px" : "-1px")};

    img {
      height: ${props => (props.inverted ? "52px" : "31px")};
    }
  }

  .menu {
    float: right;

    ${StyledButton} {
      font-size: inherit;
      padding: 0 32px;
      height: 40px;
    }

    .mobile-button {
      width: 30px;
      height: 30px;
      padding: 0;
      border: 0;
      background: transparent;
      cursor: pointer;

      svg {
        fill: ${props => (props.inverted ? "white" : "black")};
      }

      &:hover {
        svg {
          fill: ${props => (props.inverted ? "white" : "var(--primary-color)")};
        }
      }
    }
  }

  ul {
    display: flex;
    list-style: none;
    margin: 0;
    height: 30px;
    line-height: 30px;
    font-size: 16px;
    font-weight: 600;
    gap: 30px;

    li {
      a {
        color: ${props => (props.inverted ? "white" : "var(--heading-color)")};
        transition: opacity 0.3s ease;

        &:hover {
          text-decoration: none;
          opacity: 0.6;
          cursor: pointer;
        }
      }
    }

    select {
      border: none;
      background: none;
      color: ${props => (props.inverted ? "white" : "black")};
      font-size: 12px;
      font-weight: 600;
    }

    option {
      color: black;
    }
  }

  ${props =>
    props.inverted &&
    css`
      position: absolute;
      right: 0;
      left: 0;
    `};

  /* Equisoft nav items are plain 600-weight text links, no pill or border */
  .menu .secondary {
    display: flex;
    align-items: center;
    height: 40px;
  }
`;

const StyledMobileMenu = styled.div`
  visibility: hidden;
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  min-height: 100vh;
  color: #fff;
  transform: scaleY(0);
  transform-origin: 0 0;
  opacity: 0;
  transition: all 0.2s ease-in;
  background: rgba(30, 30, 30, 0.985);
  padding: 60px 0;

  .inner {
    max-width: 420px;
    margin: 0 auto;
    padding: 0 15px;
  }

  ul {
    clear: both;
    margin: 0;
    padding: 30px 30px 0 0;
    flex-direction: column;
    height: auto;
    line-height: normal;
    gap: 0;

    li {
      margin: 0;
      padding: 15px 0;
      border-bottom: 1px solid rgba(255, 255, 255, 0.2);

      a {
        color: #ccc;

        &:hover {
          color: #fff;
        }
      }
    }
  }

  button {
    float: right;
    width: 30px;
    height: 30px;
    padding: 0;
    border: 0;
    background: transparent;
    cursor: pointer;

    svg {
      fill: #fff;
    }

    &:hover {
      svg {
        fill: #fff;
      }
    }
  }

  &.open {
    visibility: visible;
    opacity: 1;
    transform: scaleY(1);
  }
`;
